import React, { useEffect, useState, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { useAuth } from "../../../contexts/AuthContext";
import ShiftLogForm, { ShiftLogFormProps } from "./ShiftLogForm";

// ---- Types ----
type MOStatus = "confirmed" | "in_progress" | "done" | "cancelled";

type MO = {
  id: number;
  name: string;
  product: string;
  planned_qty: number;
  status: MOStatus;
  scheduled_date: string | null;
  notes: string | null;
  created_by: string;
  created_at: string;
  total_a_rolls: number;
  total_b_rolls: number;
  log_count: number;
};

type ShiftLog = {
  id: number;
  mo_id: number;
  shift_id: number | null;
  shift_name: string | null;
  brand_id: number | null;
  brand_name: string | null;
  a_roll_count: number;
  b_roll_count: number;
  submitted_by: string;
  submitted_at: string;
  notes: string | null;
};

type MODetail = MO & { logs: ShiftLog[] };

const STATUS_COLORS: Record<MOStatus, "default" | "warning" | "info" | "success" | "error"> = {
  confirmed: "warning",
  in_progress: "info",
  done: "success",
  cancelled: "error",
};

const STATUS_LABEL: Record<MOStatus, string> = {
  confirmed: "Confirmed",
  in_progress: "In Progress",
  done: "Done",
  cancelled: "Cancelled",
};

const NEXT_STATUS: Partial<Record<MOStatus, MOStatus>> = {
  confirmed: "in_progress",
  in_progress: "done",
};

// ---- Main component ----
const ManufacturingPage: React.FC = () => {
  const { token } = useAuth();
  const api = process.env.REACT_APP_API_URL || "";
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const [tab, setTab] = useState(0);
  const [mos, setMos] = useState<MO[]>([]);
  const [brands, setBrands] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<MODetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Edit log dialog
  const [editLog, setEditLog] = useState<ShiftLog | null>(null);
  const [editShifts, setEditShifts] = useState<{ id: number; name: string }[]>([]);
  const [eShiftId, setEShiftId] = useState<number | "">("");
  const [eBrandId, setEBrandId] = useState<number | "">("");
  const [eARolls, setEARolls] = useState("");
  const [eBRolls, setEBRolls] = useState("");
  const [eNotes, setENotes] = useState("");
  const [eSaving, setESaving] = useState(false);

  // Delete MO confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Delete log entry confirmation
  const [deleteLogTarget, setDeleteLogTarget] = useState<ShiftLog | null>(null);
  const [deletingLog, setDeletingLog] = useState(false);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [cName, setCName] = useState("");
  const [cBrandId, setCBrandId] = useState<number | "">("");
  const [cQty, setCQty] = useState("");
  const [cDate, setCDate] = useState("");
  const [cNotes, setCNotes] = useState("");
  const [cSaving, setCSaving] = useState(false);

  const [snack, setSnack] = useState<{ open: boolean; msg: string; severity: "success" | "error" }>
    ({ open: false, msg: "", severity: "success" });

  const showSnack = (msg: string, severity: "success" | "error") =>
    setSnack({ open: true, msg, severity });

  const loadMos = useCallback(async () => {
    setLoading(true);
    try {
      const [mosRes, brandsRes] = await Promise.all([
        fetch(`${api}/mos`, { headers }),
        fetch(`${api}/brands`, { headers }),
      ]);
      if (mosRes.ok) setMos(await mosRes.json());
      if (brandsRes.ok) {
        const all = await brandsRes.json();
        setBrands(all.filter((b: any) => b.is_active));
      }
    } catch {
      showSnack("Failed to load orders", "error");
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { loadMos(); }, [loadMos]);

  const openDetail = async (mo: MO) => {
    setDetailLoading(true);
    setTab(0);
    try {
      const res = await fetch(`${api}/mos/${mo.id}`, { headers });
      if (!res.ok) throw new Error();
      setDetail(await res.json());
    } catch {
      showSnack("Failed to load MO details", "error");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleStatusChange = async (mo: MO, newStatus: MOStatus) => {
    try {
      const res = await fetch(`${api}/mos/${mo.id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error();
      await loadMos();
      if (detail?.id === mo.id) {
        const r2 = await fetch(`${api}/mos/${mo.id}`, { headers });
        setDetail(await r2.json());
      }
      showSnack("Status updated", "success");
    } catch {
      showSnack("Failed to update status", "error");
    }
  };

  const handleDeleteLog = async () => {
    if (!deleteLogTarget || !detail) return;
    setDeletingLog(true);
    try {
      const res = await fetch(`${api}/mos/${detail.id}/logs/${deleteLogTarget.id}`, { method: "DELETE", headers });
      if (!res.ok && res.status !== 204) throw new Error();
      setDeleteLogTarget(null);
      const r2 = await fetch(`${api}/mos/${detail.id}`, { headers });
      setDetail(await r2.json());
      await loadMos();
    } catch {
      showSnack("Failed to delete log entry", "error");
    } finally {
      setDeletingLog(false);
    }
  };

  const openEditLog = async (log: ShiftLog) => {
    setEditLog(log);
    setEShiftId(log.shift_id ?? "");
    setEBrandId(log.brand_id ?? "");
    setEARolls(String(log.a_roll_count));
    setEBRolls(String(log.b_roll_count));
    setENotes(log.notes ?? "");
    // Load shifts if not yet loaded
    if (editShifts.length === 0) {
      const res = await fetch(`${api}/settings/shifts`, { headers });
      if (res.ok) { const d = await res.json(); setEditShifts(d.shifts ?? []); }
    }
  };

  const handleEditLogSave = async () => {
    if (!editLog || !detail) return;
    setESaving(true);
    try {
      const selectedShift = editShifts.find(s => s.id === eShiftId);
      const selectedBrand = brands.find(b => b.id === eBrandId);
      const res = await fetch(`${api}/mos/${detail.id}/logs/${editLog.id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({
          shift_id: eShiftId || null,
          shift_name: selectedShift?.name ?? null,
          brand_id: eBrandId || null,
          brand_name: selectedBrand?.name ?? null,
          a_roll_count: parseInt(eARolls) || 0,
          b_roll_count: parseInt(eBRolls) || 0,
          notes: eNotes.trim() || null,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Save failed");
      const r2 = await fetch(`${api}/mos/${detail.id}`, { headers });
      setDetail(await r2.json());
      await loadMos();
      setEditLog(null);
      showSnack("Entry updated", "success");
    } catch (e: any) {
      showSnack(e.message || "Save failed", "error");
    } finally {
      setESaving(false);
    }
  };

  const handleCreate = async () => {
    setCSaving(true);
    try {
      const res = await fetch(`${api}/mos`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          name: cName.trim(),
          product: brands.find(b => b.id === cBrandId)?.name ?? "",
          planned_qty: parseInt(cQty),
          scheduled_date: cDate || null,
          notes: cNotes.trim() || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Create failed");
      }
      showSnack("MO created", "success");
      setCreateOpen(false);
      setCName(""); setCBrandId(""); setCQty(""); setCDate(new Date().toISOString().slice(0, 10)); setCNotes("");
      await loadMos();
    } catch (e: any) {
      showSnack(e.message || "Create failed", "error");
    } finally {
      setCSaving(false);
    }
  };

  const handleLogSubmitted = async () => {
    if (detail) {
      const r = await fetch(`${api}/mos/${detail.id}`, { headers });
      setDetail(await r.json());
    }
    await loadMos();
    showSnack("Entry submitted", "success");
  };

  const handleDeleteMO = async () => {
    if (!detail) return;
    setDeleting(true);
    try {
      const res = await fetch(`${api}/mos/${detail.id}`, { method: "DELETE", headers });
      if (!res.ok && res.status !== 204) throw new Error();
      setDeleteConfirmOpen(false);
      setDetail(null);
      await loadMos();
      showSnack("MO deleted", "success");
    } catch {
      showSnack("Failed to delete MO", "error");
    } finally {
      setDeleting(false);
    }
  };

  const produced = detail
    ? detail.logs.reduce((sum, l) => sum + l.a_roll_count + l.b_roll_count, 0)
    : 0;
  const pct = detail ? Math.min(Math.round((produced / detail.planned_qty) * 100), 100) : 0;

  const createValid = cName.trim() && cBrandId !== "" && parseInt(cQty) > 0;

  // ---- Detail view ----
  if (detail) {
    return (
      <Box sx={{ p: { xs: 0, sm: 1 } }}>
        <Box
          display="flex"
          alignItems={{ xs: "flex-start", sm: "center" }}
          flexWrap="wrap"
          gap={1}
          mb={2}
        >
          <IconButton onClick={() => setDetail(null)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ minWidth: 0, flex: { xs: 1, sm: "0 1 auto" } }}
          >
            {detail.name}
          </Typography>
          <Chip
            label={STATUS_LABEL[detail.status]}
            color={STATUS_COLORS[detail.status]}
            size="small"
          />
          <Box
            ml={{ xs: 0, sm: "auto" }}
            mt={{ xs: 1, sm: 0 }}
            display="flex"
            gap={1}
            flexWrap="wrap"
            width={{ xs: "100%", sm: "auto" }}
          >
            {NEXT_STATUS[detail.status] && (
              <Button
                variant="contained"
                size="small"
                onClick={() => handleStatusChange(detail, NEXT_STATUS[detail.status]!)}
              >
                Mark {STATUS_LABEL[NEXT_STATUS[detail.status]!]}
              </Button>
            )}
            {detail.status !== "cancelled" && detail.status !== "done" && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => handleStatusChange(detail, "cancelled")}
              >
                Cancel MO
              </Button>
            )}
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => setDeleteConfirmOpen(true)}
            >
              Delete MO
            </Button>
          </Box>
        </Box>

        {/* Progress */}
        <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <Box display="flex" gap={{ xs: 2, sm: 4 }} flexWrap="wrap" mb={1}>
            <Box>
              <Typography variant="caption" color="text.secondary">Product</Typography>
              <Typography fontWeight={500}>{detail.product}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Planned</Typography>
              <Typography fontWeight={500}>{detail.planned_qty}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Produced</Typography>
              <Typography fontWeight={500} color={produced > detail.planned_qty ? "error.main" : "inherit"}>
                {produced}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Remaining</Typography>
              <Typography fontWeight={500}>
                {Math.max(detail.planned_qty - produced, 0)}
              </Typography>
            </Box>
            {detail.scheduled_date && (
              <Box>
                <Typography variant="caption" color="text.secondary">Scheduled</Typography>
                <Typography fontWeight={500}>{detail.scheduled_date}</Typography>
              </Box>
            )}
          </Box>
          <LinearProgress
            variant="determinate"
            value={pct}
            color={produced > detail.planned_qty ? "error" : produced === detail.planned_qty ? "success" : "primary"}
            sx={{ height: 10, borderRadius: 5 }}
          />
          <Typography variant="caption" color="text.secondary">{pct}%</Typography>
        </Paper>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ mb: 2 }}
        >
          <Tab label="Log History" />
          <Tab label="Submit Entry" disabled={detail.status === "done" || detail.status === "cancelled"} />
        </Tabs>

        {tab === 0 && (
          detail.logs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No entries yet.</Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                <TableRow>
                  <TableCell>Submitted</TableCell>
                  <TableCell>Shift</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell align="right">A Rolls</TableCell>
                  <TableCell align="right">B Rolls</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell>By</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {detail.logs.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell>{new Date(l.submitted_at).toLocaleString()}</TableCell>
                    <TableCell>{l.shift_name ?? "—"}</TableCell>
                    <TableCell>{l.brand_name ?? "—"}</TableCell>
                    <TableCell align="right">{l.a_roll_count}</TableCell>
                    <TableCell align="right">{l.b_roll_count}</TableCell>
                    <TableCell>{l.notes ?? "—"}</TableCell>
                    <TableCell>{l.submitted_by}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Tooltip title="Edit entry">
                        <IconButton size="small" onClick={() => openEditLog(l)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete entry">
                        <IconButton size="small" color="error" onClick={() => setDeleteLogTarget(l)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              </Table>
            </TableContainer>
          )
        )}

        {tab === 1 && (
          <ShiftLogForm
            api={api}
            token={token ?? undefined}
            moId={detail.id}
            moName={detail.name}
            onSubmitted={handleLogSubmitted}
          />
        )}

        {/* Edit log entry dialog */}
        <Dialog open={!!editLog} onClose={() => setEditLog(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Edit Log Entry</DialogTitle>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}>
            <TextField select label="Shift" fullWidth value={eShiftId} onChange={e => setEShiftId(Number(e.target.value))}>
              <MenuItem value="">— None —</MenuItem>
              {editShifts.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
            </TextField>
            <TextField select label="Brand" fullWidth value={eBrandId} onChange={e => setEBrandId(Number(e.target.value))}>
              <MenuItem value="">— None —</MenuItem>
              {brands.map(b => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
            </TextField>
            <Box display="flex" gap={2}>
              <TextField label="A Rolls" type="number" fullWidth value={eARolls} onChange={e => setEARolls(e.target.value)} slotProps={{ htmlInput: { min: 0 } }} />
              <TextField label="B Rolls" type="number" fullWidth value={eBRolls} onChange={e => setEBRolls(e.target.value)} slotProps={{ htmlInput: { min: 0 } }} />
            </Box>
            <TextField label="Notes" fullWidth multiline rows={2} value={eNotes} onChange={e => setENotes(e.target.value)} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditLog(null)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleEditLogSave}
              disabled={eSaving || (parseInt(eARolls) === 0 && parseInt(eBRolls) === 0)}
            >
              {eSaving ? <CircularProgress size={18} /> : "Save"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete log entry confirmation dialog */}
        <Dialog open={!!deleteLogTarget} onClose={() => setDeleteLogTarget(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Delete Log Entry?</DialogTitle>
          <DialogContent>
            <Typography>
              This will permanently delete the entry submitted by <strong>{deleteLogTarget?.submitted_by}</strong> on{" "}
              {deleteLogTarget ? new Date(deleteLogTarget.submitted_at).toLocaleString() : ""}. This cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteLogTarget(null)}>Cancel</Button>
            <Button variant="contained" color="error" onClick={handleDeleteLog} disabled={deletingLog}>
              {deletingLog ? <CircularProgress size={18} /> : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete MO confirmation dialog */}
        <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Delete Manufacturing Order?</DialogTitle>
          <DialogContent>
            <Typography>
              This will permanently delete <strong>{detail.name}</strong> and all its log entries. This cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button variant="contained" color="error" onClick={handleDeleteMO} disabled={deleting}>
              {deleting ? <CircularProgress size={18} /> : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
        </Snackbar>
      </Box>
    );
  }

  // ---- MO list ----
  return (
    <Box sx={{ p: { xs: 0, sm: 1 } }}>
      <Box
        display="flex"
        alignItems={{ xs: "stretch", sm: "center" }}
        flexDirection={{ xs: "column", sm: "row" }}
        gap={1.5}
        mb={{ xs: 2, sm: 3 }}
      >
        <Typography variant="h5" fontWeight={700}>Manufacturing Orders</Typography>
        <Button startIcon={<AddIcon />} variant="contained" sx={{ ml: { xs: 0, sm: "auto" } }} onClick={() => {
          // Auto-generate next MO number
          const nums = mos.map(m => parseInt(m.name.replace(/\D/g, ""))).filter(n => !isNaN(n));
          const next = nums.length > 0 ? Math.max(...nums) + 1 : 1;
          setCName(`MO/${String(next).padStart(3, "0")}`);
          // Default to today
          setCDate(new Date().toISOString().slice(0, 10));
          setCBrandId(""); setCQty(""); setCNotes("");
          setCreateOpen(true);
        }}>
          New MO
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : mos.length === 0 ? (
        <Typography color="text.secondary">No manufacturing orders yet.</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>MO #</TableCell>
                <TableCell>Product</TableCell>
                <TableCell align="right">Planned</TableCell>
                <TableCell align="right">Produced</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Scheduled</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {mos.map((mo) => {
                const prod = mo.total_a_rolls + mo.total_b_rolls;
                const p = Math.min(Math.round((prod / mo.planned_qty) * 100), 100);
                return (
                  <TableRow key={mo.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{mo.name}</TableCell>
                    <TableCell>{mo.product}</TableCell>
                    <TableCell align="right">{mo.planned_qty}</TableCell>
                    <TableCell align="right">{prod}</TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LinearProgress
                          variant="determinate"
                          value={p}
                          sx={{ flex: 1, height: 8, borderRadius: 4 }}
                          color={prod > mo.planned_qty ? "error" : p === 100 ? "success" : "primary"}
                        />
                        <Typography variant="caption">{p}%</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={STATUS_LABEL[mo.status]} color={STATUS_COLORS[mo.status]} size="small" />
                    </TableCell>
                    <TableCell>{mo.scheduled_date ?? "—"}</TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => openDetail(mo)}>Open</Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create MO dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New Manufacturing Order</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}>
          <TextField label="MO Number" placeholder="MO/001" fullWidth value={cName} onChange={e => setCName(e.target.value)} />
          <TextField
            select label="Brand (Product)" fullWidth value={cBrandId}
            onChange={e => setCBrandId(Number(e.target.value))}
          >
            <MenuItem value="">— Select brand —</MenuItem>
            {brands.map(b => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
          </TextField>
          <TextField
            label="Planned Quantity" type="number" fullWidth value={cQty}
            onChange={e => setCQty(e.target.value)}
            slotProps={{ htmlInput: { min: 1 } }}
          />
          <TextField
            label="Scheduled Date" type="date" fullWidth value={cDate}
            onChange={e => setCDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField label="Notes" fullWidth multiline rows={2} value={cNotes} onChange={e => setCNotes(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={cSaving || !createValid}>
            {cSaving ? <CircularProgress size={18} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ManufacturingPage;
